import apiClient, { API_BASE_URL } from './apiClient';

// ============================================================
// Types — matching backend Go models for J214 (研發採購單)
// ============================================================

export interface FJ510 {
  fj510_01: string;          // 採購單號 (PK)
  fj510_11: string | null;   // 採購日期 (ISO string)
  fj510_12: string;          // 公司代號
  fj510_13: string;          // 產品代號(前6碼)
  fj510_131: string;         // 產品代號(後6碼)
  fj510_14: string;          // 產品名稱 (TEXT)
  fj510_15: string;          // 產品規格 (TEXT)
  fj510_16: string;          // 加工別 (TEXT)
  fj510_17: number;          // 針孔-內/外(幅寬英吋)
  fj510_18: number;          // 幅寬英吋2
  fj510_19: number;          // 碼重
  fj510_20: number;          // 米平方重
  fj510_21: string | null;   // 交貨日期 (ISO string)
  fj510_22: string;          // 包裝方式
  fj510_23: number;          // 數量合計 (readOnly)
  fj510_24: string;          // 備註代號
  fj510_25: string;          // 備註內容
  fj510_26: string;          // 送貨地點
  fj510_27: string;          // 單位
  fj510_29: number;          // 未稅總金額 (readOnly)
  fj510_30: string;          // 幣別
  fj510_80: string;          // 核准狀態
  fj510_92: string | null;   // 建立時間
  details?: FJ511[];
}

export interface FJ511 {
  fj511_01: string;          // 採購單號 FK
  fj511_02: number;          // 項次
  fj511_11: string;          // 產品代號
  fj511_12: string;          // 色號
  fj511_12a: string;         // CBNO代號
  fj511_13: number;          // 數量
  fj511_14: number;          // 未稅單價
  fj511_141: number;         // 含稅單價
  fj511_15: number;          // 未稅金額
  fj511_151: number;         // 含稅金額
}

// ============================================================
// Request / Response types
// ============================================================

export interface J214ListResponse {
  data: FJ510[];
  total: number;
  page: number;
  size: number;
}

export interface J214CreateRequest {
  fj510_11: string | null;
  fj510_12: string;
  fj510_13: string;
  fj510_131: string;
  fj510_14: string;
  fj510_15: string;
  fj510_16: string;
  fj510_17: number;
  fj510_18: number;
  fj510_19: number;
  fj510_20: number;
  fj510_21: string | null;
  fj510_22: string;
  fj510_24: string;
  fj510_25: string;
  fj510_26: string;
  fj510_27: string;
  fj510_30: string;
  details: Partial<FJ511>[];
}

export interface ValidationError {
  field: string;
  message: string;
}

// ============================================================
// API methods
// ============================================================

export const j214Api = {
  async list(page = 1, size = 20): Promise<J214ListResponse> {
    const res = await apiClient.get('/api/j214', { params: { page, size } });
    return res.data;
  },

  async search(keyword: string, page = 1, size = 20): Promise<J214ListResponse> {
    const res = await apiClient.get('/api/j214', { params: { page, size, keyword } });
    return res.data;
  },

  async get(id: string): Promise<FJ510> {
    const res = await apiClient.get(`/api/j214/${encodeURIComponent(id)}`);
    return res.data;
  },

  async create(data: J214CreateRequest): Promise<FJ510> {
    const res = await apiClient.post('/api/j214', data);
    return res.data;
  },

  async update(id: string, data: J214CreateRequest): Promise<FJ510> {
    const res = await apiClient.put(`/api/j214/${encodeURIComponent(id)}`, data);
    return res.data;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/api/j214/${encodeURIComponent(id)}`);
  },

  async submit(id: string): Promise<{ message: string }> {
    const res = await apiClient.post(`/api/j214/${encodeURIComponent(id)}/submit`);
    return res.data;
  },

  async approve(id: string): Promise<{ message: string }> {
    const res = await apiClient.post(`/api/j214/${encodeURIComponent(id)}/approve`);
    return res.data;
  },

  async reject(id: string): Promise<{ message: string }> {
    const res = await apiClient.post(`/api/j214/${encodeURIComponent(id)}/reject`);
    return res.data;
  },

  async writeoff(id: string, comment?: string): Promise<{ message: string }> {
    const res = await apiClient.post(`/api/j214/${encodeURIComponent(id)}/writeoff`, { comment: comment || '' });
    return res.data;
  },

  getPrintUrl(id: string): string {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
    const params = new URLSearchParams({ token });
    return `${API_BASE_URL}/api/j214/${encodeURIComponent(id)}/print?${params.toString()}`;
  },

  async getHistory(id: string): Promise<ApprovalEvent[]> {
    const res = await apiClient.get(`/api/j214/${encodeURIComponent(id)}/history`);
    return res.data.events || [];
  },
};

// ============================================================
// Approval history types
// ============================================================

export interface ApprovalEvent {
  action: string;
  user: string;
  date: string | null;
  note: string;
}
